// Global Tier 2 fault card (2026-10-08, T-195): fires over whatever page is open when the Trading Monitor has a fault the owner has not answered.
// Two choices only: FIX NOW (queues a fix request with the evidence; nothing is restarted or repaired automatically) or ADD TO TODO DOC (queues a
// docs/TODO.md entry). Polls the cheap state route once a minute while the monitor is enabled; stays silent when the monitor is off or on error.
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { tradingMonitor } from '../lib/api'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const POLL_MS = 60_000
const SEV = { critical: '#f43f5e', high: '#f59e0b', medium: '#38bdf8', low: '#94a3b8' }

export default function TradingMonitorFaultModal() {
  const [faults, setFaults] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [snoozed, setSnoozed] = useState({})     // fault id -> true: hidden until the page is reloaded (the fault stays unanswered)
  const navigate = useNavigate()
  const firstBtn = useRef(null)

  const poll = useCallback(async () => {
    try {
      const r = await tradingMonitor.state()
      setFaults(r.success && r.config?.enabled ? (r.unanswered || []) : [])
    } catch {
      setFaults([])   // a failed poll must not raise a false fault card
    }
  }, [])

  useEffect(() => {
    poll()
    const id = setInterval(poll, POLL_MS)
    return () => clearInterval(id)
  }, [poll])

  const f = faults.find(x => !snoozed[x.id])
  useEffect(() => { if (f) firstBtn.current?.focus() }, [f?.id])
  if (!f) return null

  const choose = async (choice) => {
    setBusy(true)
    setError(null)
    try {
      const r = await tradingMonitor.choose(f.id, choice)
      if (!r.success) throw new Error(r.error || 'failed')
      await poll()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const color = SEV[f.severity] || SEV.low
  const btn = { ...MONO, fontSize: '0.74rem', fontWeight: 700, minHeight: 40, padding: '0 14px', borderRadius: 10, border: '1px solid #334155', background: '#111c31', color: '#e2e8f0', cursor: 'pointer' }
  return (
    <div role="alertdialog" aria-labelledby="tm-fault-title" aria-describedby="tm-fault-body"
         style={{ position: 'fixed', inset: 0, zIndex: 95, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '72px 12px 12px', background: 'rgba(2,6,23,0.7)' }}>
      <div style={{ width: 'min(560px, 100%)', background: '#0f172a', border: `1px solid ${color}`, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: 20 }}>
        <div className="flex items-center gap-2" style={{ ...MONO, fontSize: '0.64rem', letterSpacing: '0.08em', color }}>
          <AlertTriangle className="h-3.5 w-3.5" />TIER 2 FAULT · {String(f.severity || '').toUpperCase()}{faults.length > 1 ? ` · ${faults.length} open` : ''}
        </div>
        <h2 id="tm-fault-title" style={{ margin: '6px 0', fontSize: '1.1rem', fontWeight: 650, color: '#f1f5f9' }}>{f.kind}</h2>
        <p id="tm-fault-body" style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5, color: '#cbd5e1' }}>{f.summary}</p>
        {f.runbook && <p style={{ margin: '10px 0 0', fontSize: '0.78rem', lineHeight: 1.5, color: '#94a3b8' }}>{f.runbook}</p>}
        <p style={{ ...MONO, margin: '8px 0 0', fontSize: '0.66rem', color: '#64748b' }}>first seen {f.first_seen ? new Date(f.first_seen).toLocaleTimeString() : 'n/a'}</p>
        {error && <p className="text-rose-400 text-xs mt-2 break-words">{error}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <button ref={firstBtn} disabled={busy} style={{ ...btn, background: color, color: '#04121c', borderColor: color }} onClick={() => choose('fix_now')}>[ FIX NOW ]</button>
          <button disabled={busy} style={btn} onClick={() => choose('add_todo')}>[ ADD TO TODO DOC ]</button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, borderTop: '1px solid #1e293b', paddingTop: 10 }}>
          <button style={{ ...btn, minHeight: 32, color: '#94a3b8' }} onClick={() => { setSnoozed(s => ({ ...s, [f.id]: true })) }}>Decide later</button>
          <button style={{ ...btn, minHeight: 32, color: '#94a3b8' }} onClick={() => { setSnoozed(s => ({ ...s, [f.id]: true })); navigate('/trading-monitor') }}>Open Trading Monitor</button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: '0.68rem', color: '#64748b' }}>FIX NOW queues a fix request with this evidence; nothing is restarted or changed automatically.</p>
      </div>
    </div>
  )
}
